import type { ListingStage } from '@/api/v4/project-listings.api';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { OverflowPillRows } from '@/ui/overflow-pill-rows';
import { styled } from 'goober';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getSpotlightStageLabel } from './get-spotlight-stage-label';
import { SpotlightStagePill } from './spotlight-stage-pill';
import type {
  SpotlightStagePillItem,
  SpotlightStagePillStageKey,
  StagePillKey,
} from './spotlight-stage-pill.types';
import { shouldShowStagePillCountBadge } from './spotlight-stage-pill.utils';

export type { StagePillKey } from './spotlight-stage-pill.types';

type SpotlightRenderedStagePill =
  | SpotlightStagePillItem
  | { stage: 'editorsChoice' };

const STAGE_PILL_GAP = '8px';
const STAGE_PILL_GAP_PX = 8;
const STAGE_PILL_MAX_ROWS = 2;
const STAGE_PILL_ROW_MIN_HEIGHT_PX = 32;
const STAGE_PILL_TRACK_VERTICAL_PADDING_PX = 8;
const LISTING_STAGE_PILL_ORDER: SpotlightStagePillStageKey[] = [
  'deal',
  'investment',
  'pilot',
];

/** Matches PillsTrack padding (4px top + 4px bottom) + two pill rows + row gap. */
export const STAGE_PILLS_SLOT_MIN_HEIGHT_PX =
  STAGE_PILL_TRACK_VERTICAL_PADDING_PX +
  STAGE_PILL_ROW_MIN_HEIGHT_PX * STAGE_PILL_MAX_ROWS +
  STAGE_PILL_GAP_PX * (STAGE_PILL_MAX_ROWS - 1);

const isListingStagePillKey = (
  stage: string,
): stage is SpotlightStagePillStageKey =>
  LISTING_STAGE_PILL_ORDER.includes(stage as SpotlightStagePillStageKey);

const isRenderedStagePillKey = (stage: string): stage is StagePillKey =>
  isListingStagePillKey(stage) || stage === 'editorsChoice';

interface SpotlightCompanyStagePillsProps {
  isEditorChoice: boolean;
  listingStages: ListingStage[];
  pills: SpotlightStagePillItem[];
}

export const SpotlightCompanyStagePills = ({
  isEditorChoice,
  listingStages,
  pills,
}: SpotlightCompanyStagePillsProps) => {
  const { t } = useTranslation('spotlight');
  const [isExpanded, setIsExpanded] = useState(false);
  const visiblePills = useMemo(() => {
    const listingPills = LISTING_STAGE_PILL_ORDER.flatMap(stage => {
      const pill = pills.find(candidate => candidate.stage === stage);

      return pill ? [pill] : [];
    });

    if (!isEditorChoice) {
      return listingPills;
    }

    return [...listingPills, { stage: 'editorsChoice' as const }];
  }, [isEditorChoice, pills]);

  const renderStagePill = (pill: SpotlightRenderedStagePill) => {
    const stageLabel = getSpotlightStageLabel(
      pill.stage,
      listingStages,
      t('stage.editorsChoice'),
    );
    const countAriaLabel =
      'count' in pill && shouldShowStagePillCountBadge(pill.count)
        ? t('stagePillCount', {
            count: pill.count,
            stage: stageLabel,
          })
        : undefined;

    return (
      <SpotlightStagePill
        count={'count' in pill ? pill.count : undefined}
        countAriaLabel={countAriaLabel}
        stage={pill.stage}
        stageLabel={stageLabel}
      />
    );
  };

  if (visiblePills.length === 0) {
    return null;
  }

  return (
    <OverflowPillRows
      collapsedMaxRows={STAGE_PILL_MAX_ROWS}
      gap={STAGE_PILL_GAP}
      getItemKey={pill => pill.stage}
      isExpanded={isExpanded}
      items={visiblePills}
      maxRows={STAGE_PILL_MAX_ROWS}
      reservePopoverSlot
      rowGap={STAGE_PILL_GAP}
      onToggleExpand={() => setIsExpanded(previous => !previous)}
      renderExpandControl={({ hiddenCount, onToggleExpand }) => (
        <StageOverflowTrigger
          aria-label={t('expand')}
          type="button"
          onClick={onToggleExpand}
        >
          +{hiddenCount}
        </StageOverflowTrigger>
      )}
      renderPill={pill => renderStagePill(pill)}
      renderPopoverContent={popoverPills => (
        <StagePillsPopover direction="column" gap={STAGE_PILL_GAP}>
          {popoverPills.map(pill =>
            isRenderedStagePillKey(pill.stage) ? (
              <Fragment key={pill.stage}>{renderStagePill(pill)}</Fragment>
            ) : null,
          )}
        </StagePillsPopover>
      )}
      renderPopoverTrigger={overflowCount => (
        <StageOverflowTrigger aria-label={t('expand')} type="button">
          +{overflowCount}
        </StageOverflowTrigger>
      )}
      showLessLabel={t('showLess')}
    />
  );
};

const StageOverflowTrigger = styled('button')`
  ${({ theme }) => theme.typography.spotlight.toggleButton}
  background: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  cursor: pointer;
  flex-shrink: 0;
  padding: 6px 10px;
  white-space: nowrap;
  ${({ theme }) =>
    theme.mixins.transition(
      ['background-color'],
      theme.duration.normal,
      theme.easing.out,
    )}

  &:hover {
    background: ${({ theme }) => theme.colors.spotlight.surface.accentHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }
`;

const StagePillsPopover = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.spotlight.popover};
  max-width: 240px;
  padding: 12px;
`;
